import Big from 'big.js'
import useTranslation from 'next-translate/useTranslation'
import { useCallback, useContext, useMemo } from 'react'

import { useExecuteTransactions } from '../hooks/useExecuteTransactions'

import { AppContext } from './AppContext/AppContext'
import CryptoCounter from './CryptoCounter'
import FiatCounter from './FiatCounter'
import IconContainer from './icons/IconContainer'
import { LockedMETContext } from './LockedMetContext'
import { MetronomeContext } from './MetronomeContext'
import OperationButton from './OperationButton'
import TransactionContext from './TransactionContext'

function ClaimLockedMETRewardsForm() {
  const { t } = useTranslation()
  const {
    state: {
      status: { paused },
      wallet: { connected }
    }
  } = useContext(AppContext)

  const {
    disableOnOperation,
    openTransactionDrawer,
    setDisableOnOperation,
    setOperationExecuted
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useContext<any>(MetronomeContext) // @TODO: type MetronomeContext

  const {
    claimables,
    closeOperation,
    metLocker,
    totalClaimableInUsd,
    updateLockedMETData
  } = useContext(LockedMETContext)

  const { options: transactionOptions } = useContext(TransactionContext)
  const { executeTransactions } = useExecuteTransactions()

  const hasClaimables = useMemo(
    () => claimables?.some(({ claimableAmount }) => Big(claimableAmount).gt(0)),
    [claimables]
  )

  const onSubmit = useCallback(
    function (e) {
      e.preventDefault()

      if (!metLocker || !hasClaimables) {
        return
      }

      openTransactionDrawer('claim')
      closeOperation('claim')
      setDisableOnOperation(true)
      setOperationExecuted(true)

      const _transactionOptions = {
        useEip1559: transactionOptions.useEip1559
      }

      const { emitter } = metLocker.claim(_transactionOptions)

      const operationSubject = 'esMET Rewards'
      const analyticsId = operationSubject

      executeTransactions(emitter, {
        analytics: {
          events: {
            'error': `Claim failed for ${analyticsId}`,
            'error-timeout': `Claim timeout in wallet for ${analyticsId}`,
            'result': `Claim succeeded for ${analyticsId}`,
            'transactions': `Claim initiated for ${analyticsId}`
          },
          toast: {
            close: `Closing pending tx notification for Claim of ${analyticsId}`,
            reopen: `Reopening Tx Modal for Claim of ${analyticsId}`
          },
          useEip1559: `Using EIP-1559 to Claim esMET Rewards`
        },
        events: {
          extendedStatus: {
            operation: 'claim',
            operationSubject,
            claimableAmount: claimables?.[0]?.claimableAmount,
            claimableAmountInUsd: totalClaimableInUsd,
            rewardToken: claimables?.[0]?.rewardToken
          }
        },
        onError() {
          setDisableOnOperation(false)
        },
        onSuccess() {
          setDisableOnOperation(false)
          // Trigger a refresh of the claimables data
          updateLockedMETData?.()
        }
      })
    },
    [
      claimables,
      closeOperation,
      executeTransactions,
      hasClaimables,
      metLocker,
      openTransactionDrawer,
      setDisableOnOperation,
      setOperationExecuted,
      totalClaimableInUsd,
      transactionOptions.useEip1559,
      updateLockedMETData
    ]
  )

  return (
    <form className="space-y-6" onSubmit={onSubmit}>
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">
          {t('claim-rewards.title')}
        </h3>
        <p className="text-sm text-gray-400">
          {t('claim-rewards.description')}
        </p>

        {claimables?.map(function ({
          claimableAmount,
          claimableAmountInUsd,
          rewardToken
        }) {
          return (
            <div
              className="bg-blue-secondary rounded-lg p-4"
              key={rewardToken.address}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <IconContainer height={32} name="met" width={32} />
                  <div>
                    <CryptoCounter
                      end={claimableAmount}
                      suffix={` ${rewardToken.symbol}`}
                    />
                    {claimableAmountInUsd && (
                      <div className="text-sm text-gray-400">
                        <FiatCounter end={claimableAmountInUsd} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        {/* Total Section */}
        {totalClaimableInUsd && Big(totalClaimableInUsd).gt(0) && (
          <div className="bg-blue-tertiary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">{t('total')}</span>
              <div className="text-right">
                <FiatCounter end={totalClaimableInUsd} />
              </div>
            </div>
          </div>
        )}
      </div>

      <OperationButton
        disabled={!connected || disableOnOperation || paused || !hasClaimables}
        type="submit"
      >
        {t('actions.claim')}
      </OperationButton>
    </form>
  )
}

export default ClaimLockedMETRewardsForm
